/**
 * Runtime GraphQL enum values extracted from the schema.
 *
 * Pattern: const object + derived type for each enum.
 *
 * These constants are decoupled from the schema once written. Schema additions
 * must be reflected here manually. `gql.tada` will surface most divergences as
 * type errors where these consts meet operation result types, but it cannot
 * detect missing entries on its own.
 */

export const ApplicantEmploymentType = {
  BusinessOwner: 'business_owner',
  Employee: 'employee',
  SelfEmployed: 'self_employed',
} as const;
export type ApplicantEmploymentType = (typeof ApplicantEmploymentType)[keyof typeof ApplicantEmploymentType];

export const AnalysesMethod = {
  AiModerated: 'ai_moderated',
  Moderated: 'moderated',
  Unmoderated: 'unmoderated',
} as const;
export type AnalysesMethod = (typeof AnalysesMethod)[keyof typeof AnalysesMethod];

export const ApplyDeclineProjectType = {
  Apply: 'apply',
  Decline: 'decline',
} as const;
export type ApplyDeclineProjectType = (typeof ApplyDeclineProjectType)[keyof typeof ApplyDeclineProjectType];

export const BookingRecruitLanguageFluency = {
  Beginner: 'beginner',
  Conversational: 'conversational',
  Fluent: 'fluent',
  Native: 'native',
} as const;
export type BookingRecruitLanguageFluency =
  (typeof BookingRecruitLanguageFluency)[keyof typeof BookingRecruitLanguageFluency];

export const DocPermission = {
  Delete: 'delete',
  Edit: 'edit',
  Share: 'share',
  View: 'view',
} as const;
export type DocPermission = (typeof DocPermission)[keyof typeof DocPermission];

export const DocSharingScope = {
  Email: 'email',
  Team: 'team',
  User: 'user',
} as const;
export type DocSharingScope = (typeof DocSharingScope)[keyof typeof DocSharingScope];

export const EvidenceSortBy = {
  HighestQuality: 'highest_quality',
  MostRecent: 'most_recent',
  MostRelevant: 'most_relevant',
} as const;
export type EvidenceSortBy = (typeof EvidenceSortBy)[keyof typeof EvidenceSortBy];

export const KindeSsoStrategy = {
  Oauth2AzureAd: 'oauth2_azure_ad',
  SamlCustom: 'saml_custom',
  SamlOkta: 'saml_okta',
  WsfedAzureAd: 'wsfed_azure_ad',
} as const;
export type KindeSsoStrategy = (typeof KindeSsoStrategy)[keyof typeof KindeSsoStrategy];

export const Locale = {
  Ar: 'ar',
  Cs: 'cs',
  Da: 'da',
  De: 'de',
  En: 'en',
  Es: 'es',
  Fr: 'fr',
  Hi: 'hi',
  Hu: 'hu',
  It: 'it',
  Ja: 'ja',
  Ko: 'ko',
  Nl: 'nl',
  Pt: 'pt',
  Sk: 'sk',
  Tr: 'tr',
  ZhCn: 'zh_CN',
  ZhHk: 'zh_HK',
} as const;
export type Locale = (typeof Locale)[keyof typeof Locale];

export const LocaleProficiency = {
  Beginner: 'beginner',
  Conversational: 'conversational',
  Fluent: 'fluent',
  Native: 'native',
} as const;
export type LocaleProficiency = (typeof LocaleProficiency)[keyof typeof LocaleProficiency];

export const OnboardingStatus = {
  Complete: 'complete',
  Incomplete: 'incomplete',
} as const;
export type OnboardingStatus = (typeof OnboardingStatus)[keyof typeof OnboardingStatus];

export const OperationalOffices = {
  Au: 'AU',
  Uk: 'UK',
  Us: 'US',
} as const;
export type OperationalOffices = (typeof OperationalOffices)[keyof typeof OperationalOffices];

export const ProcessingStatus = {
  InResults: 'in_results',
  NeedsReview: 'needs_review',
  Rejected: 'rejected',
  ResultsProcessing: 'results_processing',
  ResultsProcessingFailed: 'results_processing_failed',
  ReviewProcessing: 'review_processing',
} as const;
export type ProcessingStatus = (typeof ProcessingStatus)[keyof typeof ProcessingStatus];

export const ProjectStatus = {
  Active: 'active',
  Archived: 'archived',
  Completed: 'completed',
  Draft: 'draft',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

export const ProjectUserStatus = {
  Accepted: 'accepted',
  Applied: 'applied',
  Declined: 'declined',
  Expired: 'expired',
  Invited: 'invited',
} as const;
export type ProjectUserStatus = (typeof ProjectUserStatus)[keyof typeof ProjectUserStatus];

export const ReportReason = {
  CameraOff: 'camera_off',
  CommunicationIssues: 'communication_issues',
  InaccurateScreenerAnswers: 'inaccurate_screener_answers',
  MicrophoneOff: 'microphone_off',
  Others: 'others',
  ParticipantNoShow: 'participant_no_show',
  PoorInternetConnection: 'poor_internet_connection',
  SubmissionQuality: 'submission_quality',
  TechnicalIssues: 'technical_issues',
} as const;
export type ReportReason = (typeof ReportReason)[keyof typeof ReportReason];

export const ReportRequest = {
  Refund: 'refund',
  Replace: 'replace',
} as const;
export type ReportRequest = (typeof ReportRequest)[keyof typeof ReportRequest];

export const ResearcherCertificationStatus = {
  Certified: 'certified',
  InProgress: 'in_progress',
  InProgressDropped: 'in_progress_dropped',
  InReview: 'in_review',
  Ineligible: 'ineligible',
  NotCertified: 'not_certified',
  OnHold: 'on_hold',
  Started: 'started',
} as const;
export type ResearcherCertificationStatus =
  (typeof ResearcherCertificationStatus)[keyof typeof ResearcherCertificationStatus];

export const ResearcherMethods = {
  CompetitiveAnalysis: 'competitive_analysis',
  ContinuousAiModerated: 'continuous_ai_moderated',
  ContinuousResearcherModerated: 'continuous_researcher_moderated',
  Discovery: 'discovery',
  Longitudinal: 'longitudinal',
  Survey: 'survey',
  Usability: 'usability',
} as const;
export type ResearcherMethods = (typeof ResearcherMethods)[keyof typeof ResearcherMethods];

export const SegmentFilterMethod = {
  AnyOf: 'any_of',
  Contains: 'contains',
  DoesNotContain: 'does_not_contain',
  EqualTo: 'equal_to',
  GreaterOrEqualTo: 'greater_or_equal_to',
  GreaterThan: 'greater_than',
  LessOrEqualTo: 'less_or_equal_to',
  LessThan: 'less_than',
  NoneOf: 'none_of',
  NotEqualTo: 'not_equal_to',
} as const;
export type SegmentFilterMethod = (typeof SegmentFilterMethod)[keyof typeof SegmentFilterMethod];

export const SegmentMethod = {
  And: 'and',
  Or: 'or',
} as const;
export type SegmentMethod = (typeof SegmentMethod)[keyof typeof SegmentMethod];

export const SegmentSortValue = {
  Age: 'age',
  Created: 'created',
  Eligibility: 'eligibility',
  Session: 'session',
  Status: 'status',
  StatusUpdated: 'status_updated',
} as const;
export type SegmentSortValue = (typeof SegmentSortValue)[keyof typeof SegmentSortValue];

export const SubmissionRoleFilter = {
  Expert: 'expert',
  None: 'none',
  Novice: 'novice',
} as const;
export type SubmissionRoleFilter = (typeof SubmissionRoleFilter)[keyof typeof SubmissionRoleFilter];

export const SubmissionStatus = {
  Available: 'available',
  Completed: 'completed',
  Confirmed: 'confirmed',
  Excluded: 'excluded',
  HelpRequested: 'help_requested',
  InProgress: 'in_progress',
  InviteDeclined: 'invite_declined',
  Invited: 'invited',
  Offered: 'offered',
  ParticipantCancelled: 'participant_cancelled',
  PendingCheck: 'pending_check',
  PendingTechCheck: 'pending_tech_check',
  Reported: 'reported',
  TimeExpired: 'time_expired',
  Waitlisted: 'waitlisted',
} as const;
export type SubmissionStatus = (typeof SubmissionStatus)[keyof typeof SubmissionStatus];

export const TeamType = {
  Full: 'full',
  IndustryStreamsOnly: 'industry_streams_only',
} as const;
export type TeamType = (typeof TeamType)[keyof typeof TeamType];

export const TreeTestOutcomeType = {
  DirectSuccess: 'direct_success',
  Failure: 'failure',
  IndirectSuccess: 'indirect_success',
} as const;
export type TreeTestOutcomeType = (typeof TreeTestOutcomeType)[keyof typeof TreeTestOutcomeType];

export const UploadArea = {
  BookingTasks: 'booking_tasks',
  CustomAgreements: 'custom_agreements',
  Insights: 'insights',
  Profile: 'profile',
} as const;
export type UploadArea = (typeof UploadArea)[keyof typeof UploadArea];

export const UploadMimeType = {
  ApplicationPdf: 'application_pdf',
  ImageGif: 'image_gif',
  ImageJpg: 'image_jpg',
  ImagePng: 'image_png',
  ImageWebp: 'image_webp',
} as const;
export type UploadMimeType = (typeof UploadMimeType)[keyof typeof UploadMimeType];

export const UserType = {
  Admin: 'admin',
  Client: 'client',
  Participant: 'participant',
  Researcher: 'researcher',
  ShareViewer: 'share_viewer',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

export const UserMetaUxLength = {
  FiveOrMore: 'five_or_more',
  Four: 'four',
  OneOrLess: 'one_or_less',
  Three: 'three',
  Two: 'two',
} as const;
export type UserMetaUxLength = (typeof UserMetaUxLength)[keyof typeof UserMetaUxLength];
